import * as React from 'react';
import { useMemo, useRef, useState } from 'react';
import CancelIcon from '@mui/icons-material/Cancel';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { Box, TextField } from '@mui/material';
import Badge from '@mui/material/Badge';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import { styled } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import {
  ColumnsPanelTrigger,
  ExportCsv,
  ExportPrint,
  FilterPanelTrigger,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
  QuickFilterTrigger,
  Toolbar,
  ToolbarButton
} from '@mui/x-data-grid';
import {
  ExportExcel,
  GridCsvExportOptions,
  GridExcelExportOptions,
  GridExportDisplayOptions,
  GridPrintExportOptions,
  QuickFilterProps
} from '@mui/x-data-grid-premium';

type ToolbarProps = {
  children?: React.ReactNode;
  startContent?: React.ReactNode;
  printOptions?: GridPrintExportOptions & GridExportDisplayOptions;
  excelOptions?: GridExcelExportOptions & GridExportDisplayOptions;
  csvOptions?: GridCsvExportOptions & GridExportDisplayOptions;
  quickFilterProps?: QuickFilterProps;
  'data-testid'?: string;
};

export const DefaultToolbar = ({
  printOptions,
  excelOptions,
  csvOptions,
  quickFilterProps,
  children,
  startContent,
  ...restProps
}: ToolbarProps) => {
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuTriggerRef = useRef<HTMLButtonElement>(null);

  const exportMenuHided = useMemo(
    () => printOptions?.disableToolbarButton && excelOptions?.disableToolbarButton && csvOptions?.disableToolbarButton,
    [printOptions, excelOptions, csvOptions]
  );

  return (
    <Toolbar {...restProps}>
      {startContent && (
        <>
          {startContent}
          <Box sx={{ flex: 1 }} />
        </>
      )}
      <Tooltip title="Columns">
        <ColumnsPanelTrigger render={<ToolbarButton />}>
          <ViewColumnIcon fontSize="small" />
        </ColumnsPanelTrigger>
      </Tooltip>
      <Tooltip title="Filters">
        <FilterPanelTrigger
          render={(props, state) => (
            <ToolbarButton {...props} color="default">
              <Badge badgeContent={state.filterCount} color="primary" variant="dot">
                <FilterListIcon fontSize="small" />
              </Badge>
            </ToolbarButton>
          )}
        />
      </Tooltip>
      <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
      {!exportMenuHided && (
        <>
          <Tooltip title="Export">
            <ToolbarButton
              ref={exportMenuTriggerRef}
              id="export-menu-trigger"
              aria-controls="export-menu"
              aria-haspopup="true"
              aria-expanded={exportMenuOpen ? 'true' : undefined}
              onClick={() => setExportMenuOpen(true)}
            >
              <FileDownloadIcon fontSize="small" />
            </ToolbarButton>
          </Tooltip>
          <Menu
            id="export-menu"
            anchorEl={exportMenuTriggerRef.current}
            open={exportMenuOpen}
            onClose={() => setExportMenuOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            slotProps={{
              list: {
                'aria-labelledby': 'export-menu-trigger'
              }
            }}
          >
            {!printOptions?.disableToolbarButton && (
              <ExportPrint options={printOptions} render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
                Print
              </ExportPrint>
            )}
            {!csvOptions?.disableToolbarButton && (
              <ExportCsv options={csvOptions} render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
                Download as CSV
              </ExportCsv>
            )}
            {!excelOptions?.disableToolbarButton && (
              <ExportExcel options={excelOptions} render={<MenuItem />} onClick={() => setExportMenuOpen(false)}>
                Download as Excel
              </ExportExcel>
            )}
          </Menu>
          <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
        </>
      )}
      {children ? (
        <>
          {children}
          <Divider orientation="vertical" variant="middle" flexItem sx={{ mx: 0.5 }} />
        </>
      ) : null}
      <StyledQuickFilter {...quickFilterProps}>
        <QuickFilterTrigger
          render={(triggerProps, state) => (
            <Tooltip title="Search" enterDelay={0}>
              <StyledToolbarButton
                {...triggerProps}
                ownerState={{ expanded: state.expanded }}
                color="default"
                aria-disabled={state.expanded}
              >
                <SearchIcon fontSize="small" />
              </StyledToolbarButton>
            </Tooltip>
          )}
        />
        <QuickFilterControl
          render={({ ref, ...controlProps }, state) => (
            <StyledTextField
              {...controlProps}
              ownerState={{ expanded: state.expanded }}
              inputRef={ref}
              aria-label="Search"
              placeholder="Search..."
              size="small"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: state.value ? (
                    <InputAdornment position="end">
                      <QuickFilterClear
                        edge="end"
                        size="small"
                        aria-label="Clear search"
                        material={{ sx: { marginRight: -0.75 } }}
                      >
                        <CancelIcon fontSize="small" />
                      </QuickFilterClear>
                    </InputAdornment>
                  ) : null,
                  ...controlProps.slotProps?.input
                },
                ...controlProps.slotProps
              }}
            />
          )}
        />
      </StyledQuickFilter>
    </Toolbar>
  );
};

type OwnerState = {
  expanded: boolean;
};

const StyledQuickFilter = styled(QuickFilter)({
  display: 'grid',
  alignItems: 'center'
});

const StyledToolbarButton = styled(ToolbarButton)<{ ownerState: OwnerState }>(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  width: 'min-content',
  height: 'min-content',
  zIndex: 1,
  opacity: ownerState.expanded ? 0 : 1,
  pointerEvents: ownerState.expanded ? 'none' : 'auto',
  transition: theme.transitions.create(['opacity'])
}));

const StyledTextField = styled(TextField)<{
  ownerState: OwnerState;
}>(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  overflowX: 'clip',
  width: ownerState.expanded ? 260 : 'var(--trigger-width)',
  opacity: ownerState.expanded ? 1 : 0,
  transition: theme.transitions.create(['width', 'opacity'])
}));
